import { useState, useMemo } from 'react';
import { FaqFieldDataDto, WebflowFaqDto } from '@/api/generated/model';
import { useWebflowControllerGetFaqs } from '@/api/generated/webflow/webflow';
import { DEFAULT_FAQS } from '../constants';

interface Faq extends WebflowFaqDto {
  fieldData: FaqFieldDataDto;
}

// The order of slugs to display FAQs (subset for payment page)
const FAQ_SLUG_ORDER = [
  'what-does-a-catch-membership-include',
  'what-cancers-are-included-in-my-risk-assessment',
  'how-does-catch-determine-my-personalized-lifetime-cancer-risk',
  'is-cancer-actually-preventable',
  'how-do-i-cancel-my-membership',
];

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

const FAQItem = ({ question, answer, isOpen, onToggle }: FAQItemProps) => {
  return (
    <div className="border border-slate-100 rounded-[10px] overflow-hidden bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="text-sm font-medium text-slate-700 pr-4 leading-5">
          {question}
        </span>
        <span
          className={`flex-shrink-0 w-4 h-4 flex items-center justify-center text-slate-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          <svg
            width="12"
            height="8"
            viewBox="0 0 12 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1.5L6 6.5L11 1.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <div
          className="px-4 pb-4 text-sm text-slate-500 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      </div>
    </div>
  );
};

export interface FAQSectionProps {
  title?: string;
}

export const FAQSection = ({ title = "Questions? We've got answers." }: FAQSectionProps) => {
  const { data: faqsResponse, isLoading, error } = useWebflowControllerGetFaqs();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Process FAQs from Webflow API or use fallback
  const faqs = useMemo(() => {
    if (isLoading) return [];

    // If API failed or returned no data, use fallback
    if (error || !faqsResponse?.items?.length) {
      return DEFAULT_FAQS.map((faq) => ({
        question: faq.question,
        answer: faq.answer,
      }));
    }

    // Filter and sort by slug order
    const allFaqs = faqsResponse.items as Faq[];
    const orderedFaqs = FAQ_SLUG_ORDER.map((slug) =>
      allFaqs.find((faq) => faq.fieldData?.slug === slug)
    ).filter(Boolean) as Faq[];

    // Map to simple question/answer format
    return orderedFaqs.map((faq) => ({
      question: faq.fieldData.name,
      answer: faq.fieldData.answer,
    }));
  }, [faqsResponse, isLoading, error]);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <h3 className="text-xl font-bold text-slate-900 mb-4 text-center">{title}</h3>
        <div className="flex flex-col gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-[52px] bg-slate-100 rounded-[10px] animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <h3 className="text-xl font-bold text-slate-900 mb-4 text-center">{title}</h3>
      <div className="flex flex-col gap-2">
        {faqs.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            isOpen={openIndex === index}
            onToggle={() => handleToggle(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default FAQSection;
